import { useState, useEffect, useCallback } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider,
  InputAdornment, Paper, TextField, Typography, Chip, TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";

const STEPS = [500, 1000, 5000, 10000, 50000];
const HISTORY_PAGE_SIZE = 10;
const fmtAmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN"));
const fmtWhen = (d) => new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function BadBeatJackpot() {
  const token = useSelector((s) => s.loginSlice.token);

  const [currentAmount, setCurrentAmount] = useState(null);
  const [inputAmount, setInputAmount]     = useState("");
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState("");

  const fetchBBJ = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosRequest("GET", `${apiBaseUrl}/bbj`, token);
      if (res?.data?.status) {
        const amt = res.data.data.bbj_amount;
        setCurrentAmount(amt);
        setInputAmount(String(amt));
      } else {
        setError("Failed to load BBJ amount.");
      }
    } catch {
      setError("Network error while loading BBJ.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBBJ(); }, [fetchBBJ]);

  // Change history (every save from this page is logged server-side)
  const [history, setHistory]       = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage]   = useState(0);
  const [historyFrom, setHistoryFrom]   = useState("");
  const [historyTo, setHistoryTo]       = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const q = new URLSearchParams({ page: historyPage + 1, size: HISTORY_PAGE_SIZE });
      if (historyFrom) q.set("date_from", historyFrom);
      if (historyTo) q.set("date_to", historyTo);
      const res = await axiosRequest("GET", `${apiBaseUrl}/bbj/history?${q}`, token);
      setHistory(res?.data?.history || []);
      setHistoryTotal(res?.data?.pagination?.total || 0);
    } finally {
      setHistoryLoading(false);
    }
  }, [token, historyPage, historyFrom, historyTo]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleStep = (delta) => {
    const current = Number(inputAmount) || 0;
    const next = Math.max(0, current + delta);
    setInputAmount(String(next));
    setSuccess("");
    setError("");
  };

  const handleSave = async () => {
    const val = Number(inputAmount);
    if (isNaN(val) || inputAmount.trim() === "") {
      setError("Please enter a valid amount.");
      return;
    }
    if (val < 0) {
      setError("Amount cannot be negative.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await axiosRequest("PUT", `${apiBaseUrl}/bbj`, token, { bbj_amount: val });
      if (res?.data?.status) {
        setCurrentAmount(val);
        setSuccess("BBJ amount updated successfully!");
        if (historyPage === 0) fetchHistory(); else setHistoryPage(0);
      } else {
        setError(res?.data?.message || "Failed to update BBJ amount.");
      }
    } catch {
      setError("Network error while saving.");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = Number(inputAmount) !== currentAmount;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8, maxWidth: 640, mx: "auto" }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: 0.5 }}>
            Bad Beat Jackpot
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage the current jackpot pool amount
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchBBJ}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Current Amount Card */}
      <Paper
        elevation={4}
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
          color: "#fff",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -40, right: -40,
            width: 160, height: 160,
            borderRadius: "50%",
            background: "rgba(255,215,0,0.08)",
          },
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.7, letterSpacing: 2, textTransform: "uppercase" }}>
          Current Jackpot Pool
        </Typography>

        {loading ? (
          <Box mt={2}><CircularProgress sx={{ color: "#ffd700" }} /></Box>
        ) : (
          <Typography
            variant="h2"
            fontWeight={900}
            sx={{ color: "#ffd700", mt: 1, letterSpacing: 1, textShadow: "0 0 20px rgba(255,215,0,0.4)" }}
          >
            {currentAmount !== null ? Number(currentAmount).toLocaleString("en-IN") : "—"}
          </Typography>
        )}

        {currentAmount !== null && !loading && (
          <Chip
            label="LIVE"
            size="small"
            sx={{ mt: 2, background: "#ffd700", color: "#1a1a2e", fontWeight: 700, fontSize: 11, letterSpacing: 1 }}
          />
        )}
      </Paper>

      {/* Editor Card */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          Adjust Jackpot Amount
        </Typography>

        {/* Quick step buttons */}
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Quick Decrease
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {STEPS.map((s) => (
              <Button
                key={`-${s}`}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<RemoveIcon />}
                onClick={() => handleStep(-s)}
                sx={{ minWidth: 90, fontWeight: 700 }}
              >
                {s >= 1000 ? `${s / 1000}K` : s}
              </Button>
            ))}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Quick Increase
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {STEPS.map((s) => (
              <Button
                key={`+${s}`}
                variant="outlined"
                color="success"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleStep(s)}
                sx={{ minWidth: 90, fontWeight: 700 }}
              >
                {s >= 1000 ? `${s / 1000}K` : s}
              </Button>
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Manual input */}
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Or set exact amount
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="BBJ Amount ()"
            value={inputAmount}
            onChange={(e) => {
              setInputAmount(e.target.value);
              setSuccess("");
              setError("");
            }}
            inputProps={{ min: 0 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"></InputAdornment>,
            }}
          />
          {isDirty && currentAmount !== null && (
            <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
              Change: {Number(inputAmount) > currentAmount ? "+" : ""}
              {(Number(inputAmount) - currentAmount).toLocaleString("en-IN")} from current {Number(currentAmount).toLocaleString("en-IN")}
            </Typography>
          )}
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || !isDirty}
          sx={{
            fontWeight: 700,
            fontSize: 16,
            py: 1.5,
            background: "linear-gradient(135deg, #0f3460, #16213e)",
            "&:hover": { background: "linear-gradient(135deg, #16213e, #0f3460)" },
            "&:disabled": { opacity: 0.5 },
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Paper>

      {/* Change History */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight={700}>Change History</Typography>
          {historyLoading && <CircularProgress size={18} />}
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr auto" gap={1.5} mb={2} alignItems="center">
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={historyFrom}
            onChange={(e) => { setHistoryFrom(e.target.value); setHistoryPage(0); }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={historyTo}
            onChange={(e) => { setHistoryTo(e.target.value); setHistoryPage(0); }} />
          {(historyFrom || historyTo) ? (
            <Button size="small" onClick={() => { setHistoryFrom(""); setHistoryTo(""); setHistoryPage(0); }}>Clear</Button>
          ) : <span />}
        </Box>

        {!history.length ? (
          <Typography color="text.secondary" textAlign="center" py={3} fontSize={14}>
            {historyFrom || historyTo ? "No changes in this period." : "No changes recorded yet."}
          </Typography>
        ) : history.map((h) => {
          const diff = h.old_amount === null || h.old_amount === undefined ? null : h.new_amount - h.old_amount;
          return (
            <Box key={h._id} sx={{ py: 1.25, borderTop: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5 }}>
              <Chip
                size="small"
                label={diff === null ? "SET" : `${diff > 0 ? "+" : ""}${fmtAmt(diff)}`}
                color={diff === null ? "default" : diff > 0 ? "success" : "error"}
                variant="outlined"
                sx={{ fontWeight: 700, minWidth: 84 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} fontSize={15}>
                  {fmtAmt(h.old_amount)} → {fmtAmt(h.new_amount)}
                </Typography>
                <Typography color="text.secondary" fontSize={12.5} sx={{ overflowWrap: "anywhere" }}>
                  {fmtWhen(h.createdAt)}{h.changed_by ? ` · ${h.changed_by}` : ""}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {historyTotal > HISTORY_PAGE_SIZE && (
          <TablePagination
            component="div" count={historyTotal} page={historyPage} rowsPerPage={HISTORY_PAGE_SIZE}
            rowsPerPageOptions={[HISTORY_PAGE_SIZE]} onPageChange={(_, p) => setHistoryPage(p)}
            sx={{ ".MuiTablePagination-toolbar": { px: 0 } }}
          />
        )}
      </Paper>
    </Box>
  );
}
